import Image from '@tiptap/extension-image'
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react'
import { cn } from '@/lib/utils'
import { MediaControls } from './MediaControls'
import { alignAttribute, alignJustifyClass, type MediaAlign } from './mediaAlign'

// Editor-only view: hover reveals align + remove controls; the selected node
// gets a ring. Serialization is unchanged — what's stored comes from the
// extension's renderHTML, not this view.
function ImageView({ node, selected, deleteNode, updateAttributes }: NodeViewProps) {
  return (
    <NodeViewWrapper className={cn('group relative flex max-w-full', alignJustifyClass(node.attrs.align))}>
      <span className="relative inline-block">
        <img
          src={node.attrs.src}
          alt={node.attrs.alt ?? ''}
          width={node.attrs.width ?? undefined}
          height={node.attrs.height ?? undefined}
          className={cn('rounded-lg max-w-full', selected && 'ring-2 ring-primary/70')}
        />
        <MediaControls
          align={node.attrs.align}
          onAlign={(align: MediaAlign) => updateAttributes({ align })}
          onDelete={deleteNode}
          deleteLabel="Remove image"
        />
      </span>
    </NodeViewWrapper>
  )
}

// Image with width/height so authors can size images (and browsers can
// reserve space — no layout shift on the public site), plus a pointer to the
// stored full-size original so an already-cropped upload can be re-cropped.
export const ArticleImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: { default: null },
      height: { default: null },
      ...alignAttribute,
      originalSrc: {
        default: null,
        parseHTML: element => element.getAttribute('data-original-src'),
        renderHTML: attributes =>
          attributes.originalSrc ? { 'data-original-src': attributes.originalSrc } : {},
      },
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageView)
  },
})
